<template>
  <div class="orchit-dashboard-container orchit-animate-fade-up">

    <!-- --- Tab Header --- -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="orchit-card p-2 d-inline-flex bg-white rounded-pill shadow-sm border">
          <ul class="nav nav-pills custom-orchit-pills" id="dashboardTabs" role="tablist">
            <li class="nav-item" role="presentation">
              <button 
                class="nav-link rounded-pill px-4 py-2 d-flex align-items-center" 
                :class="{ active: activeTab === 'main' }" 
                @click="activeTab = 'main'" 
                type="button"
              >
                <vue-feather type="activity" size="16" class="me-2"></vue-feather>
                Main Overview
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button 
                class="nav-link rounded-pill px-4 py-2 d-flex align-items-center" 
                :class="{ active: activeTab === 'tokens' }" 
                @click="activeTab = 'tokens'" 
                type="button"
              >
                <vue-feather type="database" size="16" class="me-2"></vue-feather>
                API Token Usage
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- --- Tab Content --- -->
    <div class="tab-content" id="dashboardTabsContent">
      
      <!-- --- Main Overview Tab --- -->
      <div v-if="activeTab === 'main'" class="tab-pane fade show active">
        <!-- --- Stats Grid --- -->
        <div class="row g-4 mb-5 justify-content-center">
          <div v-for="(stat, index) in stats" :key="index" class="col-xl-2 col-lg-3 col-md-4 col-sm-6">
            <router-link :to="stat.route" class="stat-link">
              <div class="orchit-card p-4 h-100 stat-card">
                <div class="stat-icon-wrap" :style="{ backgroundColor: stat.color + '20', color: stat.color }">
                  <vue-feather :type="stat.icon" size="20"></vue-feather>
                </div>
                <div class="mt-3">
                  <h3 class="stat-value text-truncate">
                    <template v-if="isLoading">...</template>
                    <template v-else>{{ stat.value }}</template>
                  </h3>
                  <p class="stat-label text-truncate">{{ stat.label }}</p>
                </div>
              </div>
            </router-link>
          </div>
        </div>

        <!-- --- Stats Chart --- -->
        <div class="row mb-5">
          <div class="col-12">
            <div class="orchit-card p-4 overflow-hidden">
              <div class="details-header mb-4 d-flex justify-content-between align-items-center">
                <div>
                  <h2 class="chart-section-title">Ticker Analytics</h2>
                  <p class="chart-section-subtitle">Comprehensive performance overview of all system metrics</p>
                </div>
                <div class="chart-badge stat-icon-wrap" style="background-color: var(--orchit-primary-light); color: var(--orchit-primary);">
                  <vue-feather type="bar-chart-2" size="20"></vue-feather>
                </div>
              </div>
              <div class="chart-container">
                <apexchart type="bar" height="400" :options="chartOptions" :series="chartSeries"></apexchart>
              </div>
            </div>
          </div>
        </div>

        <!-- --- Secondary Stats Grid (User Breakdown & More) --- -->
        <div class="row g-4 mb-4">
          <div class="col-md-6">
            <div class="orchit-card p-4 h-100">
               <h3 class="chart-section-title mb-3" style="font-size: 1rem;">User Distribution</h3>
               <div class="d-flex gap-4">
                  <div class="user-breakdown-item p-3 rounded-3 flex-fill" style="background: rgba(16, 185, 129, 0.1); border: 1px solid rgba(16, 185, 129, 0.2);">
                    <div class="breakdown-label" style="color: #10B981; font-size: 0.75rem; font-weight: 700; text-transform: uppercase;">Paid Users</div>
                    <div class="breakdown-value" style="font-size: 1.5rem; font-weight: 800; color: var(--orchit-text-primary);">
                      <template v-if="isLoading">...</template>
                      <template v-else>{{ rawCounts.users?.paid || 0 }}</template>
                    </div>
                  </div>
                  <div class="user-breakdown-item p-3 rounded-3 flex-fill" style="background: rgba(245, 158, 11, 0.1); border: 1px solid rgba(245, 158, 11, 0.2);">
                    <div class="breakdown-label" style="color: #F59E0B; font-size: 0.75rem; font-weight: 700; text-transform: uppercase;">Free Users</div>
                    <div class="breakdown-value" style="font-size: 1.5rem; font-weight: 800; color: var(--orchit-text-primary);">
                      <template v-if="isLoading">...</template>
                      <template v-else>{{ rawCounts.users?.free || 0 }}</template>
                    </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </div>

      <!-- --- Token Usage Tab --- -->
      <div v-if="activeTab === 'tokens'" class="tab-pane fade show active">
        <!-- Filters Row -->
        <div class="orchit-card p-4 mb-4">
          <!-- Quick Filters -->
          <div class="d-flex flex-wrap gap-2 mb-4">
            <span class="text-muted small fw-bold me-2 align-self-center text-uppercase" style="letter-spacing: 0.05em;">Quick Presets:</span>
            <button 
              v-for="preset in datePresets" 
              :key="preset.id"
              class="btn btn-sm rounded-pill px-3 py-1"
              :class="activePreset === preset.id ? 'btn-primary shadow-sm' : 'btn-outline-secondary opacity-75'"
              @click="applyDatePreset(preset.id)"
              style="font-size: 0.75rem; font-weight: 700;"
            >
              {{ preset.label }}
            </button>
          </div>

          <div class="row g-3 align-items-end border-top pt-4">
            <div class="col-md-3">
              <label class="form-label small fw-bold">Start Date</label>
              <input type="date" v-model="tokenFilters.startDate" class="form-control orchit-input" @change="activePreset = ''">
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-bold">End Date</label>
              <input type="date" v-model="tokenFilters.endDate" class="form-control orchit-input" @change="activePreset = ''">
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-bold">User Type</label>
              <select v-model="tokenFilters.userType" class="form-select orchit-input">
                <option value="">All Users</option>
                <option value="internal">Internal</option>
                <option value="external">External</option>
              </select>
            </div>
            <div class="col-md-3">
              <button class="btn btn-primary w-100 orchit-btn" @click="fetchTokenUsage" :disabled="isTokensLoading">
                <vue-feather v-if="!isTokensLoading" type="filter" size="16" class="me-2"></vue-feather>
                <span v-else class="spinner-border spinner-border-sm me-2"></span>
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        <!-- Token Stats Summary -->
        <div class="row g-4 mb-4">
           <div class="col-md-3">
             <div class="orchit-card p-4 h-100 stat-card">
               <div class="stat-icon-wrap" style="background-color: rgba(124, 58, 237, 0.1); color: #7C3AED;">
                 <vue-feather type="cpu" size="20"></vue-feather>
               </div>
               <div class="mt-3">
                 <h3 class="stat-value">
                    <template v-if="isTokensLoading">...</template>
                    <template v-else>{{ formatNumber(tokenUsageTotals.total_tokens || 0) }}</template>
                 </h3>
                 <p class="stat-label">Total Tokens</p>
               </div>
             </div>
           </div>
           <div class="col-md-3">
             <div class="orchit-card p-4 h-100 stat-card">
               <div class="stat-icon-wrap" style="background-color: rgba(6, 182, 212, 0.1); color: #06B6D4;">
                 <vue-feather type="arrow-down-left" size="20"></vue-feather>
               </div>
               <div class="mt-3">
                 <h3 class="stat-value">
                    <template v-if="isTokensLoading">...</template>
                    <template v-else>{{ formatNumber(tokenUsageTotals.input_tokens || 0) }}</template>
                 </h3>
                 <p class="stat-label">Input Tokens</p>
               </div>
             </div>
           </div>
           <div class="col-md-3">
             <div class="orchit-card p-4 h-100 stat-card">
               <div class="stat-icon-wrap" style="background-color: rgba(16, 185, 129, 0.1); color: #10B981;">
                 <vue-feather type="arrow-up-right" size="20"></vue-feather>
               </div>
               <div class="mt-3">
                 <h3 class="stat-value">
                    <template v-if="isTokensLoading">...</template>
                    <template v-else>{{ formatNumber(tokenUsageTotals.output_tokens || 0) }}</template>
                 </h3>
                 <p class="stat-label">Output Tokens</p>
               </div>
             </div>
           </div>
           <div class="col-md-3">
             <div class="orchit-card p-4 h-100 stat-card">
               <div class="stat-icon-wrap" style="background-color: rgba(245, 158, 11, 0.1); color: #F59E0B;">
                 <vue-feather type="zap" size="20"></vue-feather>
               </div>
               <div class="mt-3">
                 <h3 class="stat-value">
                    <template v-if="isTokensLoading">...</template>
                    <template v-else>{{ formatNumber(tokenUsageTotals.request_count || 0) }}</template>
                 </h3>
                 <p class="stat-label">Total Requests</p>
               </div>
             </div>
           </div>
        </div>

        <!-- Token Usage Table -->
        <div class="orchit-card overflow-hidden">
          <div class="table-responsive">
            <table class="table orchit-table mb-0">
              <thead>
                <tr>
                  <th>Context / Scope</th>
                  <th>Requests</th>
                  <th>Input Tokens</th>
                  <th>Output Tokens</th>
                  <th>Total Tokens</th>
                </tr>
              </thead>
              <tbody>
                <template v-if="isTokensLoading">
                  <tr v-for="i in 5" :key="i">
                    <td v-for="j in 5" :key="j">
                      <div class="placeholder-glow">
                        <span class="placeholder col-12"></span>
                      </div>
                    </td>
                  </tr>
                </template>
                <template v-else-if="tokenUsageData.length > 0">
                  <tr v-for="(item, index) in tokenUsageData" :key="index">
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="stat-icon-wrap me-2" style="width: 32px; height: 32px; font-size: 12px; background: rgba(124, 58, 237, 0.05); color: #7C3AED;">
                          <vue-feather type="box" size="14"></vue-feather>
                        </div>
                        <div>
                          <div class="fw-bold">{{ item.scope || 'General' }}</div>
                          <div class="small text-muted">{{ item.details || 'System AI processing' }}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span class="fw-bold">{{ formatNumber(item.request_count) }}</span>
                    </td>
                    <td>{{ formatNumber(item.input_tokens) }}</td>
                    <td>{{ formatNumber(item.output_tokens) }}</td>
                    <td>
                      <span class="badge rounded-pill px-3 py-2" style="background: rgba(124, 58, 237, 0.1); color: #7C3AED; font-weight: 700; font-size: 0.85rem;">
                        {{ formatNumber(item.total_tokens) }}
                      </span>
                    </td>
                  </tr>
                </template>
                <tr v-else>
                  <td colspan="4" class="text-center py-5">
                    <div class="opacity-50 mb-3">
                      <vue-feather type="info" size="48"></vue-feather>
                    </div>
                    <h5 class="text-muted mb-0">No token usage data found</h5>
                    <p class="text-muted small">Try adjusting your filters to see more results.</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed, reactive } from 'vue';
import apiService from '@/services/apiService';
import apexchart from "vue3-apexcharts";

// --- Tab Management ---
const activeTab = ref('main');

// --- Main Overview State ---
const rawCounts = ref<any>({});
const isLoading = ref(true);

const stats = ref([
  { label: 'Workspaces', value: '0', icon: 'grid', color: '#7C3AED', route: '/all-workspaces', key: 'workspaces' },
  { label: 'Total Users', value: '0', icon: 'users', color: '#06B6D4', route: '/registered-users', key: 'users_total' },
  { label: 'AI Agents', value: '0', icon: 'target', color: '#10B981', route: '/admin/agents', key: 'agents' },
  { label: 'Total Prompts', value: '0', icon: 'command', color: '#F59E0B', route: '/prompts', key: 'prompts' },
  { label: 'Active Packages', value: '0', icon: 'package', color: '#6366F1', route: '/packages', key: 'packages' },
  { label: 'Blogs', value: '0', icon: 'book-open', color: '#D946EF', route: '/blogs', key: 'blogs' },
  { label: 'Industries', value: '0', icon: 'layers', color: '#EC4899', route: '/industries', key: 'industries' },
  { label: 'Industry Users', value: '0', icon: 'user-plus', color: '#F43F5E', route: '/industry-users', key: 'industryUsers' },
  { label: 'Knowledge Bases', value: '0', icon: 'database', color: '#F59E0B', route: '/admin/knowledge-bases', key: 'knowledgeBases' },
  { label: 'Technologies', value: '0', icon: 'cpu', color: '#10B981', route: '/technologies', key: 'technologies' },
  { label: 'Tools', value: '0', icon: 'tool', color: '#3B82F6', route: '/tools', key: 'tools' },
  { label: 'Platforms', value: '0', icon: 'monitor', color: '#64748B', route: '/platforms', key: 'platforms' },
  { label: 'Card Types', value: '0', icon: 'file-text', color: '#8B5CF6', route: '/cardtypes', key: 'cardTypes' },
  { label: 'Card Statuses', value: '0', icon: 'check-circle', color: '#14B8A6', route: '/card-statuses', key: 'cardStatuses' },
  { label: 'Card Priorities', value: '0', icon: 'flag', color: '#EF4444', route: '/cardpriorities', key: 'cardPriorities' },
  { label: 'Job Titles', value: '0', icon: 'briefcase', color: '#EAB308', route: '/job-titles', key: 'jobTitles' },
  { label: 'Company Roles', value: '0', icon: 'users', color: '#84CC16', route: '/cms-roles-permissions', key: 'companyRoles' },
  { label: 'Team Roles', value: '0', icon: 'users', color: '#0EA5E9', route: '/team-roles', key: 'teamRoles' },
]);

// --- Token Usage State ---
const isTokensLoading = ref(false);
const tokenUsageData = ref<any[]>([]);
const tokenUsageTotals = ref<any>({ total_tokens: 0, input_tokens: 0, output_tokens: 0 });
const tokenFilters = reactive({
  startDate: '',
  endDate: '',
  userType: ''
});

// --- Quick Filters Logic ---
const activePreset = ref('');
const datePresets = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'this_week', label: 'This Week' },
  { id: 'last_week', label: 'Last Week' },
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
];

const applyDatePreset = (presetId: string) => {
  activePreset.value = presetId;
  const now = new Date();
  let start = new Date();
  let end = new Date();

  // Helper to format date to YYYY-MM-DD
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  switch (presetId) {
    case 'today':
      // Both start and end are today
      break;
    case 'yesterday':
      start.setDate(now.getDate() - 1);
      end.setDate(now.getDate() - 1);
      break;
    case 'this_week':
      // Get current day (0 is Sunday, 1 is Monday...)
      const currentDayOfWeek = now.getDay();
      // Calculate diff to Monday (if Sunday, go back 6 days, otherwise go back currentDay-1)
      const diffToMonday = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;
      start.setDate(now.getDate() - diffToMonday);
      break;
    case 'last_week':
      const mondayOfThisWeek = new Date();
      const currentDayIdx = now.getDay();
      const diffToMon = currentDayIdx === 0 ? 6 : currentDayIdx - 1;
      mondayOfThisWeek.setDate(now.getDate() - diffToMon);
      
      start.setDate(mondayOfThisWeek.getDate() - 7);
      end.setDate(mondayOfThisWeek.getDate() - 1);
      break;
    case 'this_month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'last_month':
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
      break;
  }

  tokenFilters.startDate = formatDate(start);
  tokenFilters.endDate = formatDate(end);
  
  fetchTokenUsage();
};

// --- Helper Functions ---
const formatNumber = (num: number) => {
  return new Intl.NumberFormat().format(num);
};

// --- API Methods ---
const fetchDashboardData = async () => {
  try {
    isLoading.value = true;
    const response = await apiService.getDashboardCounts();
    if (response) {
      rawCounts.value = response;
      stats.value.forEach(stat => {
        if (stat.key === 'users_total') {
          stat.value = response.users?.total?.toString() || '0';
        } else {
          stat.value = response[stat.key]?.toString() || '0';
        }
      });
      chartSeries.value = [{ name: 'Count', data: stats.value.map(s => parseInt(s.value) || 0) }];
    }
  } catch (error) {
    console.error('Error fetching dashboard counts:', error);
  } finally {
    isLoading.value = false;
  }
};

const fetchTokenUsage = async () => {
  try {
    isTokensLoading.value = true;
    const params: any = {};
    if (tokenFilters.startDate) params.startDate = tokenFilters.startDate;
    if (tokenFilters.endDate) params.endDate = tokenFilters.endDate;
    if (tokenFilters.userType) params.userType = tokenFilters.userType;

    const response = await apiService.getTokenUsageTotals(params);
    if (response) {
      tokenUsageTotals.value = {
        total_tokens: response.overall_totals?.total_tokens || 0,
        input_tokens: response.overall_totals?.prompt_tokens || 0,
        output_tokens: response.overall_totals?.completion_tokens || 0,
        request_count: response.overall_totals?.request_count || 0
      };
      
      // Convert grouped_totals object to array for the table
      if (response.grouped_totals) {
        tokenUsageData.value = Object.entries(response.grouped_totals).map(([key, val]: [string, any]) => ({
          scope: val.label || key.charAt(0).toUpperCase() + key.slice(1),
          input_tokens: val.prompt_tokens || 0,
          output_tokens: val.completion_tokens || 0,
          total_tokens: val.total_tokens || 0,
          request_count: val.request_count || 0,
          details: `${val.request_count || 0} requests processed`
        }));
      } else {
        tokenUsageData.value = [];
      }
    }
  } catch (error) {
    console.error('Error fetching token usage:', error);
  } finally {
    isTokensLoading.value = false;
  }
};

// --- Chart Options ---
const chartOptions = computed(() => ({
  chart: {
    type: 'bar' as const,
    height: 400,
    toolbar: { show: false },
    zoom: { enabled: false },
    animations: { enabled: true, easing: 'easeinout', speed: 800 },
    fontFamily: 'Manrope, sans-serif'
  },
  plotOptions: {
    bar: { distributed: true, borderRadius: 8, horizontal: false, columnWidth: '60%' }
  },
  colors: stats.value.map(s => s.color),
  dataLabels: {
    enabled: true,
    style: { fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: 700 },
    offsetY: -20,
    dropShadow: { enabled: false }
  },
  legend: { show: false },
  grid: { borderColor: 'rgba(124, 58, 237, 0.05)', strokeDashArray: 4 },
  xaxis: {
    categories: stats.value.map(s => s.label),
    labels: {
      rotate: -45,
      style: { fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: '10px', colors: '#6B7280' }
    },
    axisBorder: { show: false },
    axisTicks: { show: false }
  },
  yaxis: {
    labels: { style: { fontFamily: 'Manrope, sans-serif', colors: '#6B7280' } }
  },
  tooltip: {
    theme: 'light',
    custom: function({ series, seriesIndex, dataPointIndex, w }: any) {
      const label = w.globals.labels[dataPointIndex];
      const val = series[seriesIndex][dataPointIndex];
      const color = w.globals.colors[dataPointIndex];
      return `
        <div class="orchit-chart-tooltip" style="border-top: 3px solid ${color}">
          <div class="tooltip-label">${label}</div>
          <div class="tooltip-value">${val}</div>
        </div>
      `;
    }
  }
}));

const chartSeries = ref([{ name: 'Count', data: stats.value.map(s => parseInt(s.value) || 0) }]);

onMounted(() => {
  fetchDashboardData();
  applyDatePreset('this_month'); // Default to current month as per API behavior
});
</script>

<style scoped lang="scss">
.orchit-dashboard-container {
  padding: 1.5rem;
  max-width: 1600px;
  margin: 0 auto;
}

.custom-orchit-pills {
  .nav-link {
    color: var(--orchit-text-secondary);
    font-weight: 600;
    font-size: 0.9rem;
    transition: all 0.3s ease;
    border: 1px solid transparent;

    &:hover {
      color: var(--orchit-primary);
      background: rgba(124, 58, 237, 0.05);
    }

    &.active {
      background: var(--orchit-primary) !important;
      color: white !important;
      box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
    }
  }
}

.stat-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(124, 58, 237, 0.05);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--orchit-shadow-lg);
    border-color: var(--orchit-primary);
  }

  .stat-icon-wrap {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .stat-value {
    font-size: 1.5rem;
    font-weight: 800;
    margin: 0;
    color: var(--orchit-text-primary);
    letter-spacing: -0.02em;
  }
  .stat-label {
    color: var(--orchit-text-secondary);
    margin: 0;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }
}

.orchit-input {
  border-radius: 10px;
  border: 1px solid rgba(124, 58, 237, 0.1);
  padding: 0.6rem 1rem;
  font-weight: 500;
  color: var(--orchit-text-primary);
  transition: all 0.2s ease;

  &:focus {
    border-color: var(--orchit-primary);
    box-shadow: 0 0 0 4px rgba(124, 58, 237, 0.1);
  }
}

.orchit-btn {
  border-radius: 10px;
  padding: 0.6rem 1.5rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 58, 237, 0.2);
  }
}

.orchit-table {
  thead th {
    background: rgba(124, 58, 237, 0.02);
    border-bottom: 2px solid rgba(124, 58, 237, 0.05);
    color: var(--orchit-text-secondary);
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.05em;
    padding: 1.25rem 1.5rem;
  }

  tbody td {
    padding: 1.25rem 1.5rem;
    vertical-align: middle;
    color: var(--orchit-text-primary);
    font-weight: 500;
    border-bottom: 1px solid rgba(124, 58, 237, 0.03);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: rgba(124, 58, 237, 0.01);
  }
}

.chart-section-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--orchit-text-primary);
  margin: 0;
  letter-spacing: -0.01em;
}

.chart-section-subtitle {
  font-size: 0.875rem;
  color: var(--orchit-text-secondary);
  margin: 0.25rem 0 0 0;
}

:deep(.orchit-chart-tooltip) {
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(124, 58, 237, 0.1);

  .tooltip-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: var(--orchit-text-secondary);
    letter-spacing: 0.05em;
  }
  .tooltip-value {
    font-size: 1.25rem;
    font-weight: 800;
    color: var(--orchit-text-primary);
    margin-top: 2px;
  }
}

.stat-link {
  text-decoration: none !important;
  display: block;
  height: 100%;
}
</style>